import React from "react"
import {useAccessToken} from "../../services/auth"
import {Navigate, useParams} from "react-router-dom"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {
	Application,
	getApplication,
	isBeneficialOwnerResource,
	isBusinessApplicationDocument,
	reevaluableApplicationStatuses,
} from "../../resources/application"
import {useOrgSettings} from "../../services/orgAuth"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import ReevaluateApplicationContainer from "./ReevaluateApplicationContainer"
import ReevaluateBeneficialOwnerContainer from "./ReevaluateBeneficialOwnerContainer"
import ReevaluateOfficerContainer from "./ReevaluateOfficerContainer"
import {OkDocument} from "../../resources/common"
import {Err} from "../../components/Err/Err"
import {useIsAllowResubmit} from "../../services/permission"

export enum ApplicationResubmitForms {
	individualForm = "individual-form",
	businessForm = "business-form",
	officerForm = "officer-form",
	beneficialOwnerForm = "beneficial-owner-form",
}

interface Props {
	children(props: {application: OkDocument<Application>; enableReevaluateApplication: boolean}): JSX.Element
}

function ReevaluatePage({children}: Props) {
	const {applicationId = ""} = useParams<{applicationId: string}>()
	const accessToken = useAccessToken()
	const applicationAsyncResult = useAsyncResult(() => getApplication(accessToken, applicationId))

	return (
		<HasPermission resource="orgSettings" action="get">
			<HasPermission resource="application.resubmit" action="create">
				<AsyncResultComponent asyncResult={applicationAsyncResult}>
					{({value: application}) => {
						if (!reevaluableApplicationStatuses.includes(application.data.attributes.status)) {
							return <Navigate to={`/applications/${application.data.id}`} replace />
						}
						const orgSettingsAsyncResult = useOrgSettings(application.data.relationships.org.data.id)
						return (
							<AsyncResultComponent asyncResult={orgSettingsAsyncResult}>
								{({value: orgSettings}) => {
									return useIsAllowResubmit(
										orgSettings.attributes.generalSettings.enableReevaluateApplication,
										application.data.relationships.org.data.id,
										application.data.attributes.assignee
									) ? (
										<React.Fragment>
											{children({
												application: application,
												enableReevaluateApplication: orgSettings.attributes.generalSettings.enableReevaluateApplication,
											})}
										</React.Fragment>
									) : (
										<Navigate to={`/applications/${application.data.id}`} replace />
									)
								}}
							</AsyncResultComponent>
						)
					}}
				</AsyncResultComponent>
			</HasPermission>
		</HasPermission>
	)
}

export function ReevaluateApplicationPage() {
	return (
		<ReevaluatePage>
			{({application, enableReevaluateApplication}) => {
				return (
					<ReevaluateApplicationContainer
						application={application}
						enableReevaluateApplication={enableReevaluateApplication}
					/>
				)
			}}
		</ReevaluatePage>
	)
}

export function ReevaluateOfficerPage() {
	return (
		<ReevaluatePage>
			{({application, enableReevaluateApplication}) => {
				if (!isBusinessApplicationDocument(application)) {
					return <Err err={"Provided Application Should Be Business Application"} />
				}

				if (!application.data.attributes.officer || !application.data.relationships.officer) {
					return <Err err={"Provided Application Should Have Officer"} />
				}

				return (
					<ReevaluateOfficerContainer
						application={application}
						enableReevaluateApplication={enableReevaluateApplication}
						officer={application.data.attributes.officer}
						officerId={application.data.relationships.officer.data.id}
					/>
				)
			}}
		</ReevaluatePage>
	)
}

export function ReevaluateBeneficialOwnerPage() {
	const {beneficialOwnerId = ""} = useParams<{beneficialOwnerId: string}>()

	return (
		<ReevaluatePage>
			{({application, enableReevaluateApplication}) => {
				if (!isBusinessApplicationDocument(application)) {
					return <Err err="Provided Application Should Be Business Application" />
				}
				const beneficialOwnerResource = application.included?.find(
					(e) => e.type === "beneficialOwner" && e.id === beneficialOwnerId
				)

				if (!beneficialOwnerResource || !isBeneficialOwnerResource(beneficialOwnerResource)) {
					return <Err err="Provided Beneficial Owner is incorrect" />
				}

				return (
					<ReevaluateBeneficialOwnerContainer
						application={application}
						enableReevaluateApplication={enableReevaluateApplication}
						beneficialOwnerResource={beneficialOwnerResource}
					/>
				)
			}}
		</ReevaluatePage>
	)
}
